// Global
import React, { useCallback, useMemo, useState } from 'react';
import { LinkA11yWrapperProps } from 'components/helpers/LinkA11yWrapper/_types';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';

type LeadingIconSet = {
  default: string;
  hover?: string;
  active?: string;
  disabled?: string;
};

export type ActionCardCtaProps = Omit<LinkA11yWrapperProps, 'leadingIcon'> & {
  disabled?: boolean;
  leadingIcon?: LeadingIconSet;
};

const ActionCardCta = ({
  disabled,
  leadingIcon,
  onClick,
  ...props
}: ActionCardCtaProps): React.JSX.Element => {
  const [iconState, setIconState] = useState<'default' | 'hover' | 'active'>('default');

  const resolvedIconSrc = useMemo(() => {
    if (!leadingIcon) return undefined;
    if (disabled) return leadingIcon.disabled ?? leadingIcon.default;
    if (iconState === 'active') return leadingIcon.active ?? leadingIcon.default;
    if (iconState === 'hover') return leadingIcon.hover ?? leadingIcon.default;
    return leadingIcon.default;
  }, [leadingIcon, disabled, iconState]);

  const iconHandlers = useMemo(
    () =>
      leadingIcon && !disabled
        ? {
            onMouseEnter: () => setIconState('hover'),
            onMouseLeave: () => setIconState('default'),
            onMouseDown: () => setIconState('active'),
            onMouseUp: () => setIconState('hover'),
          }
        : undefined,
    [leadingIcon, disabled]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        e.preventDefault();
        return false;
      }
      return onClick ? onClick(e) : true;
    },
    [disabled, onClick]
  );

  return (
    <LinkA11yWrapper
      {...props}
      {...iconHandlers}
      aria-disabled={disabled ? true : undefined}
      leadingIcon={resolvedIconSrc}
      onClick={handleClick}
    />
  );
};

export default ActionCardCta;
